import { Controller } from "@hotwired/stimulus";
import NotificationsSubscription from "../../channels/notifications_subscription";

export default class extends Controller {
  static identifier: string;

  static setIdentifier(identifier: string) {
    this.identifier = identifier;
  }
  static values = {
    threadId: String,
    currentUserId: Number,
  };
  static targets = ["typingIndicator"];

  declare readonly threadIdValue: string;
  declare readonly currentUserIdValue: number;
  declare readonly typingIndicatorTarget: HTMLElement;
  declare readonly hasTypingIndicatorTarget: boolean;

  private notificationsSubscription!: NotificationsSubscription;
  private typingUsers: Map<number, string> = new Map();
  private boundCallback: ((data: any) => void) | null = null;

  connect() {
    console.log(
      "Thread messages controller connected for thread:",
      this.threadIdValue,
    );
    this.subscribeToChannel();
  }

  disconnect() {
    if (this.boundCallback && this.notificationsSubscription) {
      this.notificationsSubscription.removeCallback(this.boundCallback);
    }
  }

  subscribeToChannel() {
    this.notificationsSubscription = NotificationsSubscription.getInstance();

    // Create bound callback so we can remove it later
    this.boundCallback = (data: any): void => {
      console.log("Received message via ActionCable:", data);

      if (data.message && this.isMessageForCurrentThread(data.message)) {
        this.insertMessage(data.message);
      } else if (data.typing && data.typing.thread_id === this.threadIdValue) {
        this.handleTypingIndicator(data.typing);
      } else if (
        data.read_status &&
        data.read_status.thread_id === this.threadIdValue
      ) {
        this.updateReadReceipts(data.read_status);
      }
    };

    this.notificationsSubscription.addCallback(this.boundCallback);
  }

  isMessageForCurrentThread(message: any): boolean {
    // Check if the message belongs to the current thread
    return (
      message.message_thread_encoded_id === this.threadIdValue ||
      message.message_thread_id === this.threadIdValue ||
      message.thread_id === this.threadIdValue ||
      (message.message_thread &&
        message.message_thread.encoded_id === this.threadIdValue)
    );
  }

  handleTypingIndicator(typing: any) {
    const userId = typing.user_id;
    const userName = typing.user_name || "Someone";
    const isTyping = typing.is_typing;

    console.log("Typing indicator received:", {
      userId,
      userName,
      isTyping,
      currentUserId: this.currentUserIdValue,
      threadId: typing.thread_id,
      currentThreadId: this.threadIdValue,
    });

    // Don't show typing indicator for current user
    if (userId === this.currentUserIdValue) {
      console.log("Ignoring typing indicator from current user");
      return;
    }

    if (isTyping) {
      this.typingUsers.set(userId, userName);
      console.log(
        "Added typing user:",
        userName,
        "Total typing:",
        this.typingUsers.size,
      );
    } else {
      this.typingUsers.delete(userId);
      console.log(
        "Removed typing user:",
        userName,
        "Total typing:",
        this.typingUsers.size,
      );
    }

    this.updateTypingIndicator();
  }

  updateTypingIndicator() {
    if (!this.hasTypingIndicatorTarget) {
      console.log("No typing indicator target found!");
      return;
    }

    if (this.typingUsers.size === 0) {
      console.log("Clearing typing indicator");
      this.typingIndicatorTarget.textContent = "\u00A0"; // Non-breaking space to maintain height
      return;
    }

    const names = Array.from(this.typingUsers.values());
    let text = "";

    if (names.length === 1) {
      text = `${names[0]} is typing...`;
    } else if (names.length === 2) {
      text = `${names[0]} and ${names[1]} are typing...`;
    } else {
      text = `${names[0]} and ${names.length - 1} others are typing...`;
    }

    console.log("Showing typing indicator:", text);
    this.typingIndicatorTarget.textContent = text;
  }

  updateReadReceipts(readStatusData: any) {
    console.log("Updating read receipts:", readStatusData);

    // Find all messages from the current user that are older than the read timestamp
    const messagesContainer = this.element.querySelector(".overflow-y-auto");
    if (!messagesContainer) return;

    // Find all current user's messages with unread checkmarks (gray single check)
    const unreadMessages = messagesContainer.querySelectorAll(
      ".flex.items-start.justify-end",
    );

    unreadMessages.forEach((messageElement) => {
      // Find the gray single checkmark SVG
      const grayCheckmark = messageElement.querySelector("svg.text-gray-400");
      if (!grayCheckmark) return; // Already read or no checkmark

      // Replace with green double checkmark
      const readCheckmarkHTML = `
        <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13l4 4L23 7" opacity="0.6"></path>
        </svg>
      `;

      grayCheckmark.outerHTML = readCheckmarkHTML;
    });
  }

  insertMessage(messageData: any) {
    // Create the message HTML based on whether it's from the current user
    // Check both sender_user_id (numeric) and sender_user_encoded_id (string like "cf_user_...")
    const isCurrentUser =
      messageData.sender_user_id == this.currentUserIdValue ||
      (messageData.sender_user_encoded_id &&
        messageData.sender_user_encoded_id.includes(
          this.currentUserIdValue.toString(),
        ));
    const isSystemMessage =
      messageData.type === "EventMessage" || messageData.is_system_message;

    // Skip "messages_seen" system messages - we show read receipts instead
    if (isSystemMessage && messageData.event_type === "messages_seen") {
      return;
    }

    let messageHTML = "";

    if (isSystemMessage) {
      // System message - centered italic text (join/leave events only)
      messageHTML = `
        <div class="flex justify-center py-2">
          <div class="text-sm text-gray-500 italic">
            ${messageData.message || messageData.body || ""}
          </div>
        </div>
      `;
    } else if (isCurrentUser) {
      // Current user's message - right aligned with read receipt
      const isRead = messageData.is_read || false;
      const readReceiptHTML = isRead
        ? `
        <!-- Double check mark in green (read) -->
        <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13l4 4L23 7" opacity="0.6"></path>
        </svg>
      `
        : `
        <!-- Single check mark in gray (sent but not read) -->
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
      `;

      messageHTML = `
        <div class="flex items-start justify-end mb-4">
          <div class="pr-4">
            <div class="text-sm bg-blue-500 text-white p-3 rounded-lg rounded-tr-none border border-blue-600 shadow-md mb-1">
              ${messageData.message || messageData.body || ""}
            </div>
            <div class="flex items-center justify-end gap-2">
              <div class="text-xs text-slate-500 font-medium">
                Just now
              </div>
              ${readReceiptHTML}
            </div>
          </div>
          <div class="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium">
            ${messageData.initials || "?"}
          </div>
        </div>
      `;
    } else {
      // Other user's message - left aligned
      messageHTML = `
        <div class="flex items-start mb-4">
          <div class="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium">
            ${messageData.initials || "?"}
          </div>
          <div class="pl-4">
            <div class="text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 p-3 rounded-lg rounded-tl-none border border-slate-200 dark:border-slate-700 shadow-md mb-1">
              ${messageData.message || messageData.body || ""}
            </div>
            <div class="flex items-center justify-between">
              <div class="text-xs text-slate-500 font-medium">
                Just now
              </div>
            </div>
          </div>
        </div>
      `;
    }

    // Find the messages container and append the new message
    const messagesContainer = this.element.querySelector(".overflow-y-auto");
    if (messagesContainer) {
      messagesContainer.insertAdjacentHTML("beforeend", messageHTML);

      // Scroll to bottom with smooth animation
      requestAnimationFrame(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      });
    }
  }
}
